/**
 * Wallet Page Loader
 * Pre-loads wallet data before rendering
 */

import type { LoaderFunctionArgs } from 'react-router-dom';
import { requireAuth, loaderCache } from './helpers';
import {
  trackRouteLoad,
  trackRouteError,
  startRouteTimer,
  calculateDataSize,
} from '@/utils/route-analytics';
import { getWalletBalance, type WalletBalance } from '@/services/walletService';
import {
  mockTransactions,
  mockPaymentMethods,
} from '@/modules/wallet/pages/data';

export interface WalletData {
  balance: {
    totalBalance: number;
    availableBalance: number;
    pendingBalance: number;
    heldBalance: number;
    currency: string;
    totalEarned: number;
    totalSpent: number;
  };
  recentTransactions: Array<{
    id: string;
    type: string;
    amount: number;
    currency: string;
    status: string;
    description: string;
    timestamp: string;
  }>;
  paymentMethods: Array<{
    id: string;
    type: string;
    name: string;
    details: string;
    isDefault: boolean;
  }>;
}

/**
 * Fetch wallet data from API with fallback to mock
 */
async function fetchWalletData(): Promise<WalletData> {
  // Check cache first
  const cached = loaderCache.get<WalletData>('wallet');
  if (cached) {
    return cached;
  }

  let balance: WalletBalance | null = null;
  try {
    balance = await getWalletBalance();
  } catch {
    // API not available — fall through to mock
  }

  const data: WalletData = balance
    ? {
        balance: {
          totalBalance: Number(balance.total_balance),
          availableBalance: Number(balance.available_balance),
          pendingBalance: Number(balance.pending_balance),
          heldBalance: Number(balance.held_balance),
          currency: balance.currency,
          totalEarned: Number(balance.total_earned) || 0,
          totalSpent: Number(balance.total_spent) || 0,
        },
        // Transactions & payment methods still use mock until those APIs are wired
        recentTransactions:
          mockTransactions as WalletData['recentTransactions'],
        paymentMethods: mockPaymentMethods as WalletData['paymentMethods'],
      }
    : {
        balance: {
          totalBalance: 0,
          availableBalance: 0,
          pendingBalance: 0,
          heldBalance: 0,
          currency: 'USD',
          totalEarned: 0,
          totalSpent: 0,
        },
        recentTransactions:
          mockTransactions as WalletData['recentTransactions'],
        paymentMethods: mockPaymentMethods as WalletData['paymentMethods'],
      };

  // Cache for 1 minute (wallet data should be fresh)
  loaderCache.set('wallet', data, 60 * 1000);

  return data;
}

/**
 * Wallet loader function
 */
export async function walletLoader(args: LoaderFunctionArgs) {
  const getElapsed = startRouteTimer();

  try {
    // Check authentication
    await requireAuth(args.request);

    // Fetch wallet data
    const data = await fetchWalletData();

    // Track performance
    const loadTime = getElapsed();
    const dataSize = calculateDataSize(data);
    trackRouteLoad('/wallet', loadTime, dataSize);

    // Return data directly (React Router loaders don't need to be wrapped)
    return data;
  } catch (error) {
    // If it's a redirect Response, let it propagate
    if (error instanceof Response) {
      throw error;
    }

    trackRouteError('/wallet', error as Error);
    throw error;
  }
}
